/**
 * Particle systems: snow, rain, confetti, sparks, smoke… drawn by particle
 * clips. The simulation is a pure function of the clip's settings and time —
 * every particle's path has a closed form, seeded per particle — so the
 * editor can seek anywhere and the export draws exactly the same frames.
 */

import { gradientCss, parseGradient, sampleGradient, type Gradient } from './paint';

export const PARTICLE_EMITTERS = ['point', 'line', 'box', 'ring'] as const;
export type ParticleEmitter = (typeof PARTICLE_EMITTERS)[number];

export const PARTICLE_SHAPES = ['circle', 'square', 'star', 'spark', 'confetti', 'glow', 'ring'] as const;
export type ParticleShape = (typeof PARTICLE_SHAPES)[number];

export interface ParticleProps {
  /** Size of the particle field in project pixels (positions below are fractions of it). */
  width: number;
  height: number;
  /** Where particles are born: a point, a line, a box or the edge of an ellipse. */
  emitter: ParticleEmitter;
  /** Emitter centre (0..1 of the field) and, for line / box / ring, its size (0..1 of the field). */
  emitX: number;
  emitY: number;
  emitWidth: number;
  emitHeight: number;
  /** Particles born per second, and at once when the clip starts. */
  rate: number;
  burst: number;
  /** Start as if the emitter had already been running for a lifetime (snow and rain fill the frame at once). */
  prewarm: boolean;
  /** Seconds each particle lives, and how much that varies (0..1). */
  lifetime: number;
  lifetimeVariance: number;
  /** Launch speed in px/s and its variance (0..1). */
  speed: number;
  speedVariance: number;
  /** Launch direction in degrees clockwise from up (0 = up, 90 = right, 180 = down), and the spread around it. */
  direction: number;
  spread: number;
  /** Constant pull in px/s² (gravity down, wind to the right; negative for up / left). */
  gravity: number;
  wind: number;
  /** Air resistance: how fast particles lose their launch speed (per second, 0 = none). */
  drag: number;
  /** Sideways sway: amplitude in px and frequency in Hz. */
  wobble: number;
  wobbleFrequency: number;
  /** Size in px at birth and at death, and how much it varies (0..1). */
  size: number;
  sizeEnd: number;
  sizeVariance: number;
  /**
   * Colours picked at random per particle; colorEnd ('' = none) is what they
   * fade to over their life. A gradient (paint.ts) gives each particle a
   * colour at a random point along it.
   */
  colors: string[];
  colorEnd: string;
  /** Peak opacity, and the fraction of a life spent fading in and out. */
  opacity: number;
  fadeIn: number;
  fadeOut: number;
  shape: ParticleShape;
  /** Spin in degrees per second and its variance (0..1, also randomizes the direction). */
  spin: number;
  spinVariance: number;
  /** Add light (fire, sparks, fireflies) instead of covering what is behind. */
  additive: boolean;
  /** Changes the random pattern. */
  seed: number;
}

export const DEFAULT_PARTICLES: ParticleProps = {
  width: 1920,
  height: 1080,
  emitter: 'point',
  emitX: 0.5,
  emitY: 0.5,
  emitWidth: 0.2,
  emitHeight: 0.2,
  rate: 60,
  burst: 0,
  prewarm: false,
  lifetime: 2,
  lifetimeVariance: 0.3,
  speed: 300,
  speedVariance: 0.4,
  direction: 0,
  spread: 360,
  gravity: 0,
  wind: 0,
  drag: 0,
  wobble: 0,
  wobbleFrequency: 1,
  size: 12,
  sizeEnd: 4,
  sizeVariance: 0.4,
  colors: ['#ffffff'],
  colorEnd: '',
  opacity: 1,
  fadeIn: 0.05,
  fadeOut: 0.4,
  shape: 'circle',
  spin: 0,
  spinVariance: 0,
  additive: false,
  seed: 1,
};

/** Particle presets for the editor and MCP (field size is set to the project's when added). */
export const PARTICLE_PRESETS: { id: string; label: string; particles: Partial<ParticleProps> }[] = [
  {
    id: 'snow',
    label: 'Snow',
    particles: { emitter: 'line', emitX: 0.5, emitY: -0.03, emitWidth: 1.1, rate: 40, prewarm: true, lifetime: 9, lifetimeVariance: 0.2, speed: 90, speedVariance: 0.5, direction: 180, spread: 20, wind: 8, wobble: 30, wobbleFrequency: 0.4, size: 9, sizeEnd: 9, sizeVariance: 0.6, colors: ['#ffffff'], opacity: 0.9, fadeIn: 0.05, fadeOut: 0.1, shape: 'glow' },
  },
  {
    id: 'rain',
    label: 'Rain',
    particles: { emitter: 'line', emitX: 0.55, emitY: -0.05, emitWidth: 1.3, rate: 220, prewarm: true, lifetime: 0.9, lifetimeVariance: 0.15, speed: 1500, speedVariance: 0.2, direction: 190, spread: 2, gravity: 600, size: 3, sizeEnd: 3, sizeVariance: 0.3, colors: ['#a5c8ff'], opacity: 0.55, fadeIn: 0, fadeOut: 0.05, shape: 'spark' },
  },
  {
    id: 'confetti',
    label: 'Confetti',
    particles: { emitter: 'line', emitX: 0.5, emitY: 1.02, emitWidth: 0.5, rate: 0, burst: 260, lifetime: 4, lifetimeVariance: 0.3, speed: 1300, speedVariance: 0.35, direction: 0, spread: 50, gravity: 700, drag: 1.2, wobble: 20, wobbleFrequency: 1.5, size: 18, sizeEnd: 18, sizeVariance: 0.4, colors: ['#ff4d6d', '#ffd43b', '#4dabf7', '#69db7c', '#b197fc', '#ffffff'], opacity: 1, fadeIn: 0, fadeOut: 0.15, shape: 'confetti', spin: 360, spinVariance: 1 },
  },
  {
    id: 'sparks',
    label: 'Sparks',
    particles: { emitter: 'point', emitX: 0.5, emitY: 0.6, rate: 120, lifetime: 1.1, lifetimeVariance: 0.5, speed: 650, speedVariance: 0.6, direction: 0, spread: 120, gravity: 900, drag: 0.6, size: 5, sizeEnd: 1, sizeVariance: 0.5, colors: ['#ffd27a', '#ff9f43'], colorEnd: '#ff3b1f', opacity: 1, fadeIn: 0, fadeOut: 0.5, shape: 'spark', additive: true },
  },
  {
    id: 'fire',
    label: 'Fire',
    particles: { emitter: 'line', emitX: 0.5, emitY: 0.9, emitWidth: 0.12, rate: 140, lifetime: 1.2, lifetimeVariance: 0.4, speed: 260, speedVariance: 0.4, direction: 0, spread: 18, gravity: -120, drag: 0.4, wobble: 14, wobbleFrequency: 2, size: 70, sizeEnd: 10, sizeVariance: 0.4, colors: ['#ffd166', '#ff9f1c'], colorEnd: '#c1121f', opacity: 0.8, fadeIn: 0.1, fadeOut: 0.6, shape: 'glow', additive: true },
  },
  {
    id: 'smoke',
    label: 'Smoke',
    particles: { emitter: 'line', emitX: 0.5, emitY: 0.95, emitWidth: 0.15, rate: 18, prewarm: true, lifetime: 6, lifetimeVariance: 0.3, speed: 170, speedVariance: 0.4, direction: 0, spread: 16, wind: 20, drag: 0.15, wobble: 25, wobbleFrequency: 0.3, size: 90, sizeEnd: 380, sizeVariance: 0.3, colors: ['#b8b8b8', '#9a9a9a'], opacity: 0.35, fadeIn: 0.2, fadeOut: 0.6, shape: 'glow' },
  },
  {
    id: 'bubbles',
    label: 'Bubbles',
    particles: { emitter: 'line', emitX: 0.5, emitY: 1.05, emitWidth: 1, rate: 10, prewarm: true, lifetime: 7, lifetimeVariance: 0.3, speed: 180, speedVariance: 0.4, direction: 0, spread: 8, wobble: 25, wobbleFrequency: 0.6, size: 40, sizeEnd: 50, sizeVariance: 0.6, colors: ['#d0f4ff'], opacity: 0.8, fadeIn: 0.1, fadeOut: 0.1, shape: 'ring' },
  },
  {
    id: 'fireflies',
    label: 'Fireflies',
    particles: { emitter: 'box', emitX: 0.5, emitY: 0.55, emitWidth: 1, emitHeight: 0.8, rate: 8, prewarm: true, lifetime: 5, lifetimeVariance: 0.4, speed: 30, speedVariance: 0.8, direction: 0, spread: 360, wobble: 40, wobbleFrequency: 0.25, size: 14, sizeEnd: 14, sizeVariance: 0.5, colors: ['#e9ff70', '#ffe066'], opacity: 1, fadeIn: 0.3, fadeOut: 0.4, shape: 'glow', additive: true },
  },
  {
    id: 'stars',
    label: 'Star burst',
    particles: { emitter: 'point', emitX: 0.5, emitY: 0.5, rate: 0, burst: 60, lifetime: 1.6, lifetimeVariance: 0.3, speed: 700, speedVariance: 0.5, direction: 0, spread: 360, drag: 2, size: 26, sizeEnd: 6, sizeVariance: 0.4, colors: ['#ffe066', '#ffffff'], opacity: 1, fadeIn: 0, fadeOut: 0.5, shape: 'star', spin: 180, spinVariance: 1, additive: true },
  },
  {
    id: 'dust',
    label: 'Dust',
    particles: { emitter: 'box', emitX: 0.5, emitY: 0.5, emitWidth: 1, emitHeight: 1, rate: 30, prewarm: true, lifetime: 6, lifetimeVariance: 0.5, speed: 12, speedVariance: 1, direction: 90, spread: 360, wind: 4, wobble: 12, wobbleFrequency: 0.2, size: 3, sizeEnd: 3, sizeVariance: 0.7, colors: ['#fff3d6'], opacity: 0.6, fadeIn: 0.3, fadeOut: 0.3, shape: 'circle' },
  },
];

/** Most particles alive at once (rate × lifetime + burst is capped to this when drawing). */
export const MAX_LIVE_PARTICLES = 4000;

const clampNum = (v: unknown, lo: number, hi: number, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;
const isHex = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
/** A #rrggbb colour, or a gradient whose stops are (particles pick a colour along it). */
const particlePaint = (v: unknown): string | null => {
  if (isHex(v)) return v.toLowerCase();
  const g = parseGradient(v);
  return g && g.stops.every((s) => isHex(s.color)) ? gradientCss(g) : null;
};

/** Keeps known fields with valid values (missing ones take the defaults). */
export function sanitizeParticles(raw: unknown): ParticleProps {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const d = DEFAULT_PARTICLES;
  const n = (k: keyof ParticleProps, lo: number, hi: number) => clampNum(r[k], lo, hi, d[k] as number);
  const colors = Array.isArray(r.colors) ? r.colors.flatMap((c) => particlePaint(c) ?? []).slice(0, 8) : [];
  return {
    width: n('width', 1, 8000),
    height: n('height', 1, 8000),
    emitter: (PARTICLE_EMITTERS as readonly string[]).includes(r.emitter as string) ? (r.emitter as ParticleEmitter) : d.emitter,
    emitX: n('emitX', -1, 2),
    emitY: n('emitY', -1, 2),
    emitWidth: n('emitWidth', 0, 3),
    emitHeight: n('emitHeight', 0, 3),
    rate: n('rate', 0, 2000),
    burst: Math.round(n('burst', 0, 2000)),
    prewarm: r.prewarm === true,
    lifetime: n('lifetime', 0.05, 30),
    lifetimeVariance: n('lifetimeVariance', 0, 1),
    speed: n('speed', 0, 10000),
    speedVariance: n('speedVariance', 0, 1),
    direction: n('direction', -360, 360),
    spread: n('spread', 0, 360),
    gravity: n('gravity', -10000, 10000),
    wind: n('wind', -10000, 10000),
    drag: n('drag', 0, 20),
    wobble: n('wobble', 0, 2000),
    wobbleFrequency: n('wobbleFrequency', 0, 20),
    size: n('size', 0.5, 1000),
    sizeEnd: n('sizeEnd', 0, 1000),
    sizeVariance: n('sizeVariance', 0, 1),
    colors: colors.length ? colors : [...d.colors],
    colorEnd: particlePaint(r.colorEnd) ?? '',
    opacity: n('opacity', 0, 1),
    fadeIn: n('fadeIn', 0, 1),
    fadeOut: n('fadeOut', 0, 1),
    shape: (PARTICLE_SHAPES as readonly string[]).includes(r.shape as string) ? (r.shape as ParticleShape) : d.shape,
    spin: n('spin', -3600, 3600),
    spinVariance: n('spinVariance', 0, 1),
    additive: r.additive === true,
    seed: Math.round(n('seed', 0, 1e6)),
  };
}

/** A particle at one moment, in field pixels. */
export interface ParticleState {
  x: number;
  y: number;
  size: number;
  /** Rotation in radians (spark: the direction of travel). */
  angle: number;
  /** Speed in px/s (spark streak length). */
  velocity: number;
  alpha: number;
  color: string;
  /** Confetti: how far the piece has flipped (-1..1, its apparent width). */
  flip: number;
}

/** Deterministic 0..1 random number for particle `i`'s channel `ch`. */
function rand(seed: number, i: number, ch: number): number {
  let h = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(i + 1, 0xc2b2ae35) ^ Math.imul(ch + 1, 0x27d4eb2f);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function hexRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** A particle's colour from a colour field: the colour, or one at random position `r` along a gradient. */
function pickColor(v: string, r: number): string {
  if (!v.includes('gradient(')) return v;
  let g = gradients.get(v);
  if (g === undefined) {
    if (gradients.size > 64) gradients.clear();
    gradients.set(v, (g = parseGradient(v)));
  }
  return g ? sampleGradient(g, r) : '#ffffff';
}
const gradients = new Map<string, Gradient | null>();

function mixHex(a: string, b: string, t: number): string {
  if (!b || t <= 0) return a;
  const [r0, g0, b0] = hexRgb(a);
  const [r1, g1, b1] = hexRgb(b);
  const m = (x: number, y: number) => Math.round(x + (y - x) * Math.min(1, t));
  return `rgb(${m(r0, r1)},${m(g0, g1)},${m(b0, b1)})`;
}

/** Displacement after `t` s of launch speed `v0` under constant acceleration `a` and drag `k` (closed form). */
function travel(v0: number, a: number, k: number, t: number): { d: number; v: number } {
  if (k < 1e-6) return { d: v0 * t + 0.5 * a * t * t, v: v0 + a * t };
  const e = Math.exp(-k * t);
  const term = a / k;
  return { d: ((v0 - term) * (1 - e)) / k + term * t, v: (v0 - term) * e + term };
}

/** Emission time window: particles born before `-lead` never show (prewarmed emitters start a lifetime early). */
function emissionLead(p: ParticleProps): number {
  return p.prewarm ? p.lifetime * (1 + p.lifetimeVariance) : 0;
}

/**
 * Every particle alive `t` seconds into the clip (clip-local, after speed).
 * Burst particles are born at 0; the stream at `rate` per second from 0
 * (or a lifetime earlier when prewarmed, so a prewarmed field is already
 * there before the clip starts, while a transition brings the clip in).
 */
export function particlesAt(p: ParticleProps, t: number): ParticleState[] {
  const out: ParticleState[] = [];
  const maxLife = p.lifetime * (1 + p.lifetimeVariance);
  const lead = emissionLead(p);
  if (t + lead < 0) return out;
  const budget = { left: MAX_LIVE_PARTICLES };
  // Burst: indices 0..burst-1, born at time 0.
  for (let i = 0; i < p.burst && budget.left > 0; i++) push(p, i, 0, t, out, budget);
  if (p.rate > 0) {
    // Stream: particle j (0-based) is born at j / rate - lead.
    const first = Math.max(0, Math.floor((t + lead - maxLife) * p.rate));
    const last = Math.floor((t + lead) * p.rate);
    // Newest first under the cap, so a capped emitter drops its oldest particles.
    const from = Math.max(first, last - budget.left + 1);
    for (let j = from; j <= last && budget.left > 0; j++) push(p, p.burst + j, j / p.rate - lead, t, out, budget);
  }
  return out;
}

function push(p: ParticleProps, i: number, born: number, t: number, out: ParticleState[], budget: { left: number }) {
  const s = p.seed;
  const life = p.lifetime * (1 + p.lifetimeVariance * (rand(s, i, 0) * 2 - 1));
  const age = t - born;
  if (age < 0 || age >= life || life <= 0) return;
  const u = age / life;
  // Birth position on the emitter (field pixels).
  const cx = p.emitX * p.width;
  const cy = p.emitY * p.height;
  const ew = p.emitWidth * p.width;
  const eh = p.emitHeight * p.height;
  let x0 = cx;
  let y0 = cy;
  const r1 = rand(s, i, 1);
  const r2 = rand(s, i, 2);
  if (p.emitter === 'line') x0 = cx + (r1 - 0.5) * ew;
  else if (p.emitter === 'box') {
    x0 = cx + (r1 - 0.5) * ew;
    y0 = cy + (r2 - 0.5) * eh;
  } else if (p.emitter === 'ring') {
    const a = r1 * Math.PI * 2;
    x0 = cx + (Math.cos(a) * ew) / 2;
    y0 = cy + (Math.sin(a) * eh) / 2;
  }
  // Launch velocity: direction is clockwise from up.
  const dirDeg = p.direction + (rand(s, i, 3) - 0.5) * p.spread;
  const dir = (dirDeg * Math.PI) / 180;
  const v0 = p.speed * (1 + p.speedVariance * (rand(s, i, 4) * 2 - 1));
  const vx0 = Math.sin(dir) * v0;
  const vy0 = -Math.cos(dir) * v0;
  const mx = travel(vx0, p.wind, p.drag, age);
  const my = travel(vy0, p.gravity, p.drag, age);
  // Sway perpendicular to the launch direction.
  const phase = rand(s, i, 5) * Math.PI * 2;
  const sway = p.wobble ? p.wobble * Math.sin(phase + age * p.wobbleFrequency * Math.PI * 2) - p.wobble * Math.sin(phase) : 0;
  const x = x0 + mx.d + Math.cos(dir) * sway;
  const y = y0 + my.d + Math.sin(dir) * sway;
  const sizeK = 1 + p.sizeVariance * (rand(s, i, 6) * 2 - 1);
  const size = Math.max(0, (p.size + (p.sizeEnd - p.size) * u) * sizeK);
  const fadeIn = p.fadeIn > 0 ? Math.min(1, u / p.fadeIn) : 1;
  const fadeOut = p.fadeOut > 0 ? Math.min(1, (1 - u) / p.fadeOut) : 1;
  const alpha = p.opacity * Math.max(0, Math.min(fadeIn, fadeOut));
  if (alpha <= 0.002 || size <= 0) return;
  const base = pickColor(p.colors[Math.floor(rand(s, i, 7) * p.colors.length)] ?? '#ffffff', rand(s, i, 12));
  const spinSign = p.spinVariance > 0 && rand(s, i, 8) < p.spinVariance / 2 ? -1 : 1;
  const spin = p.spin * spinSign * (1 + p.spinVariance * (rand(s, i, 9) - 0.5));
  const spinAngle = rand(s, i, 10) * Math.PI * 2 * Math.min(1, p.spinVariance) + (spin * age * Math.PI) / 180;
  const angle = p.shape === 'spark' ? Math.atan2(my.v, mx.v) : spinAngle;
  budget.left--;
  out.push({
    x,
    y,
    size,
    angle,
    velocity: Math.hypot(mx.v, my.v),
    alpha,
    color: mixHex(base, p.colorEnd && pickColor(p.colorEnd, rand(s, i, 13)), u),
    flip: p.shape === 'confetti' ? Math.cos(phase + age * (2 + 4 * rand(s, i, 11)) * Math.PI) : 1,
  });
}

/** The subset of the 2D canvas API particles are drawn with (browser and @napi-rs/canvas). */
export interface ParticleCanvas {
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(a: number): void;
  scale(x: number, y: number): void;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  fill(): void;
  stroke(): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number): { addColorStop(o: number, c: string): void };
  fillStyle: unknown;
  strokeStyle: unknown;
  lineWidth: number;
  lineCap: unknown;
  globalAlpha: number;
  globalCompositeOperation: unknown;
}

/** Bitmap size (project px) of a particle clip: its field. */
export function particleBox(p: Pick<ParticleProps, 'width' | 'height'>): { width: number; height: number } {
  return { width: Math.max(1, Math.round(p.width)), height: Math.max(1, Math.round(p.height)) };
}

/** Transparent colour of the same hue, for gradient edges. */
function clear(color: string): string {
  if (color.startsWith('#')) {
    const [r, g, b] = hexRgb(color);
    return `rgba(${r},${g},${b},0)`;
  }
  return color.replace(/^rgb\((.*)\)$/, 'rgba($1,0)');
}

/**
 * Draws the particles alive `t` seconds into the clip onto a canvas of
 * particleBox(p) × scale pixels (which should start transparent).
 */
export function drawParticles(ctx: ParticleCanvas, p: ParticleProps, t: number, scale: number) {
  const list = particlesAt(p, t);
  ctx.save();
  ctx.globalCompositeOperation = p.additive ? 'lighter' : 'source-over';
  for (const q of list) {
    const r = (q.size / 2) * scale;
    if (r < 0.05) continue;
    ctx.save();
    ctx.globalAlpha = q.alpha;
    ctx.translate(q.x * scale, q.y * scale);
    switch (p.shape) {
      case 'square':
        ctx.rotate(q.angle);
        ctx.fillStyle = q.color;
        ctx.fillRect(-r, -r, 2 * r, 2 * r);
        break;
      case 'confetti':
        ctx.rotate(q.angle);
        ctx.scale(Math.max(0.08, Math.abs(q.flip)), 1);
        ctx.fillStyle = q.color;
        ctx.fillRect(-r, -r * 0.55, 2 * r, r * 1.1);
        break;
      case 'star':
        ctx.rotate(q.angle);
        ctx.fillStyle = q.color;
        ctx.beginPath();
        for (let k = 0; k < 10; k++) {
          const rr = k % 2 ? r * 0.42 : r;
          const a = -Math.PI / 2 + (k * Math.PI) / 5;
          if (k) ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
          else ctx.moveTo(Math.cos(a) * rr, Math.sin(a) * rr);
        }
        ctx.closePath();
        ctx.fill();
        break;
      case 'spark': {
        // A streak behind the particle, as long as the distance it covers in ~1/30 s.
        const len = Math.max(r * 2, q.velocity * scale * 0.033);
        ctx.rotate(q.angle);
        ctx.strokeStyle = q.color;
        ctx.lineWidth = Math.max(0.5, r);
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(-len, 0);
        ctx.lineTo(0, 0);
        ctx.stroke();
        break;
      }
      case 'glow': {
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
        g.addColorStop(0, q.color);
        g.addColorStop(0.35, q.color);
        g.addColorStop(1, clear(q.color));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 'ring':
        ctx.strokeStyle = q.color;
        ctx.lineWidth = Math.max(0.75, r * 0.14);
        ctx.beginPath();
        ctx.arc(0, 0, Math.max(0.1, r - ctx.lineWidth / 2), 0, Math.PI * 2);
        ctx.stroke();
        break;
      default:
        ctx.fillStyle = q.color;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();
  }
  ctx.restore();
}

/** Clip-local simulation time (seconds) at timeline time `t` µs: clip speed scales it. */
export function particleTime(clip: { startUs: number; speed: number }, t: number): number {
  return ((t - clip.startUs) / 1e6) * (clip.speed || 1);
}
